/**
 * THE SPACES REGISTRY — one entry per Command Center category.
 *
 * The Command Center used to be "Championships, plus a Pursuits tab". It is now
 * a set of SPACES: each category owns the whole surface while it is selected —
 * the filter bar, every view, the selection, the CSV export and the map all
 * re-scope to it. Adding, reordering or retiring a space is an edit to THIS
 * FILE alone; no component enumerates categories.
 *
 * Two data planes back the spaces, and each entry declares which one it reads:
 *
 *  - `source: 'championships'` → `useChampionships()` / `GET /api/championships`
 *    (HubSpot custom object 2-64425303). One space uses it: Championships.
 *  - `source: 'pursuits'` → `usePursuits(line)` / `GET /api/pursuits?line=<line>`
 *    (the JWT proxy to sr-pursuit-tracker). Config-driven; `line` selects the
 *    engine. Rows are adapted into the normalized championship record shape by
 *    `useCategoryUniverse`, so every existing view consumes them unchanged.
 *
 * `status: 'pending-data'` is the HONEST state for a space whose line is not
 * sourced yet: the tab is real and reachable, it says the data is being fed in,
 * and it never fetches a line that isn't there or shows a fabricated number.
 * Drop the flag the day the line returns records — nothing else changes.
 *
 * THE RAIL IS PURELY ADDITIVE (Brian, 2026-08-04). The Championships space is
 * the default and behaves EXACTLY as the Command Center did before the rail
 * existed — every view, every write action, and the same copy, verbatim. That
 * is why this registry carries `lead` / `exportTitle` / `recordsLabel` for it:
 * the page reads them instead of generating new wording for a surface that must
 * not change. `verify-spaces.mjs` §7 asserts the whole set.
 *
 * WRITES ARE CHAMPIONSHIP-ONLY. `writes: true` is set on exactly one space:
 * crown / Submit-for-Win / the Champion-to-Close dossier all write
 * `champion-write` / `champion-update` against 2-64425303, which no pursuit row
 * has. Every other space is read-only and hides those controls entirely.
 */

/**
 * Every view the Command Center can render. `dated: true` marks a view whose
 * whole organizing spine is the effective EVENT DATE (the rolling 7/30/60/90
 * lens, the time buckets, the month grid, just-concluded). Company- and
 * deal-sourced pursuit rows carry no event date, so those views would be one
 * enormous "NO DATE" bucket — they are left out of a pursuit space rather than
 * rendered empty.
 */
export const VIEW_DEFS = [
  /**
   * THE GYM PARTNERSHIP PIPELINE — the Cheer space's own view, and the only
   * view in the catalog that is declared by exactly one space. A cheer gym is a
   * HIERARCHY (gym → teams → titles) and a partnership FUNNEL at the same time,
   * which no shared view models, so it gets its own. Because a space renders
   * only the views it declares, no other space can grow this tab.
   */
  { key: 'partnership', label: 'Gym Partnerships', gyms: true,
    use: 'Every cheer gym as a partnership: target → in pursuit → contract signed → jewelry live. Expand a gym to see its teams and the titles they have won.' },
  /* THE WORK ORDER — the same gyms, ordered by the evidence rather than by
     name, with the reason on the row. The partnership board answers "where is
     everything"; this answers "who do I call first, and what do I open with",
     which is the question a rep actually has on a Monday. */
  { key: 'workorder', label: 'Work Order', gyms: true,
    use: 'The call list. Every gym ranked on what we can evidence — a competitor gone quiet, a premium ring price, a championship footprint — with the reason and the receipt on each row.' },
  {key:'queue',label:'Work queue',use:'A focused page of opportunities, each with a named owner and next action.'},
  { key: 'stages', label: 'Pursuit Stages', dated: true,
    use: 'The default lens. Everything grouped by how far away it is — just concluded, imminent, closing, horizon — so the top of the board is what to work today.' },
  { key: 'worklist', label: 'Worklist · Now/Soon/Later', dated: true,
    use: 'The same records as a flat, sortable list scoped to one time window. Use it when you want to work a period rather than scan the whole board.' },
  { key: 'board', label: 'Pursuit Board',
    use: 'Kanban by pursuit status — not started, to pursue, outreach, won. Use it to see where things are stuck rather than when they happen.' },
  { key: 'gaps', label: 'Gaps',
    /* The lead sentence no longer enumerates championship-only buckets in a
       space that draws neither a date nor a champion — GapsBoard builds it from
       the buckets the space actually raises. The tab number is DISTINCT records
       in at least one bucket, so it can never exceed the universe. */
    use: 'The records that cannot be worked as they stand. This is the fix-it queue; the number on the tab is how many records are in at least one gap (a record with three problems still counts once).' },
  /**
   * THE ASSIGNMENT ENGINE'S WORKLIST — who has nobody on them, and the rep the
   * win history says has PROVEN that sport and region. Championship-only, and
   * for the same reason the crown console is: the reassignment writes
   * `sr_assigned_rep` on HubSpot 2-64425303, which no pursuit row has a record
   * in. `champs: true` keeps it out of SHARED_VIEWS, so no other space can grow
   * the tab.
   */
  { key: 'assignments', label: 'Assignments', champs: true,
    use: 'Records with nobody on them, each with the rep the win history says has actually proven that sport and region. It proposes; you assign.' },
  /**
   * ⚠️ THE PREPARATION WORKLIST — the view the whole dataset exists to feed.
   *
   * Brian: "now you understand the challenge of crowning a champion but doing so
   * with preparation. its the reason this has to be done."
   *
   * Crowning after the fact is not the job. By the time a title is announced the
   * school is already being called, and the ring goes to whoever was READY — who
   * already had the crest, the colours, the rep and the artwork standing by. A
   * record that is merely CORRECT is worth little; one that is correct AND
   * PREPARED is the order.
   *
   * So this ranks by READINESS × TIME rather than either alone. A championship
   * 400 days out with nothing done is fine; one in nine days with no owner is an
   * order about to be lost. Sorting on readiness alone buries exactly the rows
   * that can still be saved.
   *
   * `champs: true` — it depends on the crown/assignment path, which only exists
   * against 2-64425303, so no pursuit space can grow the tab.
   */
  /* NOT `dated`. Readiness treats a MISSING date as its own urgent state — a
   * championship nobody can prepare for because nobody knows the day — so it
   * does not belong to the date-spine set that a space without dates drops. */
  { key: 'readiness', label: 'Readiness', champs: true,
    use: 'What is coming, and whether we are ready for it — ranked by how soon it lands against how much is still missing. A gap here is the reason a specific order will be lost on a specific day.' },
  /**
   * HUNTED — everything the hunt has found, in one place.
   *
   * Brian: "There should also be a tab that has all the info that the gpt finds
   * in it."
   *
   * The finds were only ever visible in the moment: a bar that vanished when
   * the selection cleared, or the row you happened to be looking at. So a
   * search that had already been paid for left no trace anybody could go back
   * to, and "did we already look at this?" had no answer.
   *
   * Championship-only for the same reason Assignments is — it reads the hunt
   * provenance on HubSpot 2-64425303, which no pursuit row has a record in.
   */
  { key: 'hunted', label: 'Hunted', champs: true,
    use: 'Everything the champion hunt has looked at, with its source — found, waiting on a result, or searched and nothing there. Answers "did we already check this?"' },
  { key: 'calendar', label: 'Calendar', dated: true,
    use: 'A month grid of when championships actually happen, coloured by status. Use it to plan a trip or a week, not to work a queue.' },
  { key: 'justwon', label: 'Just Won', dated: true,
    use: 'Crowned in the last stretch, plus events that have just concluded and should have a champion by now. This is where a ring conversation starts.' },
  { key: 'manager', label: 'Manager',
    use: 'Coverage from above — how much of the book has an owner and a date, broken down by sport, level and rep. A manager view, not a working queue.' },
  { key: 'map', label: 'Map',
    use: 'The same records geographically, shaded by status. Click a state to scope every other tab to it.' },
  { key: 'artworkcreated', label: 'Artwork Created', champs: true,
    use: 'Saved original artwork on its championship record, with exact versions, who did the work, and the next outreach action. Drafts are not sent email.' },
]

/**
 * One line saying what a tab is FOR, rendered under the tab bar.
 *
 * Brian: "a clear use of each tab in the app."
 *
 * Eleven tabs whose names are nouns — Board, Gaps, Stages, Worklist, Manager —
 * do not tell a rep which one to open, and the difference between Stages and
 * Worklist (same records, different question) is invisible from the label
 * alone. A tab nobody understands is a tab nobody opens, which is the same
 * outcome as not building it.
 *
 * Deliberately answers "when would I open this?" rather than "what is this?" —
 * a description that only restates the label is worse than none, because it
 * looks like help and isn't.
 */
export function viewUse(key) {
  return VIEW_BY_KEY[key]?.use || ''
}

export const VIEW_BY_KEY = Object.fromEntries(VIEW_DEFS.map((v) => [v.key, v]))

/** Every view key the catalog knows, shared and space-specific alike. */
export const VIEW_KEYS = VIEW_DEFS.map((v) => v.key)

/**
 * THE SHARED VIEWS — the ones any space may render. A view flagged `gyms` (the
 * Cheer pipeline) or `champs` (the assignment worklist) is one space's own
 * surface and is deliberately NOT in this set, so adding it to the catalog
 * cannot leak it into another space.
 */
const SHARED_VIEWS = VIEW_DEFS.filter((v) => !v.gyms && !v.champs)

/** Every SHARED view — the set a dated space renders. */
export const ALL_VIEWS = SHARED_VIEWS.map((v) => v.key)

/**
 * THE CHAMPIONSHIPS SPACE'S VIEWS — every shared view, plus the assignment
 * worklist, whose write only exists against 2-64425303. Canonical catalog
 * order, so the tab lands next to Gaps rather than at the end.
 */
export const CHAMPIONSHIP_VIEWS = VIEW_DEFS.filter((v) => !v.gyms).map((v) => v.key)
/** The date-independent views — what a pursuit space can honestly render. */
export const UNDATED_VIEWS = SHARED_VIEWS.filter((v) => !v.dated).map((v) => v.key)

/**
 * A DEADLINE-DRIVEN pursuit space. An RFP has no event date, but it does have a
 * CLOSE date — the submission deadline — so the date-spine views are honest
 * here: the worklist buckets bids by how soon they close, the calendar puts them
 * on the month grid, and the 7/30/60/90 lens becomes "closing in the next N
 * days". `justwon` stays out: a solicitation has no winner to crown.
 */
export const DEADLINE_VIEWS = SHARED_VIEWS
  .filter((v) => v.key !== 'justwon')
  .map((v) => v.key)

/**
 * THE CHEER SPACE'S VIEWS — its own gym-partnership pipeline FIRST (so the
 * space opens on the funnel, not on a table), then the shared undated views. A
 * gym has no event date: the titles its teams win do, but they belong to the
 * championships plane, so the 7/30/60/90 lens stays out.
 */
/* ⚠️ `partnership` STAYS FIRST — the space must open exactly as it always has.
 * The Work Order is ADDITIVE: a second lens on the same universe, not a
 * replacement for the pipeline Brian designed. `viewsFor()` orders by the
 * catalog anyway, but declaring it in the same order keeps the two from
 * disagreeing and makes the intent readable here. */
export const GYM_VIEWS = ['partnership', 'workorder', ...UNDATED_VIEWS]

const PENDING = 'pending-data'

/**
 * A space whose records are ORGANIZATIONS RUNNING A RECOGNITION PROGRAM rather
 * than events with a winner. Corporate is the first; police / fire / government
 * / military are the same shape the day their lines are sourced. It is what
 * turns off the championship framing (sport, event date, countdown, champion,
 * incumbent ring vendor) and turns on the employee-recognition columns
 * (headcount, industry, HQ, website, pursuit level).
 */
export const EMPLOYEE_RECOGNITION = 'employee-recognition'

/**
 * The other two ORGANIZATION kinds, defined in `recognition.js` and re-exported
 * here so a consumer needs one import to ask "is this an event space or an
 * organization space?".
 *
 *  - `community-hero` (POLICE + FIRE) — a department running SR's Community Hero
 *    recognition program: ONE nominated hero per precinct, recognised for years
 *    of service, ring or pendant provided at no cost.
 *  - `greek-pursuit` (GREEK) — a chapter or national organization with PRODUCT
 *    PURSUITS tracked against it (product · owner · pursuit date · won date).
 */
export { COMMUNITY_HERO, GREEK_PURSUIT } from './recognition.js'
import { COMMUNITY_HERO, GREEK_PURSUIT } from './recognition.js'

/**
 * CHEER IS ITS OWN ANIMAL (Brian, 2026-08-06) — a GYM-PARTNERSHIP MACHINE.
 *
 * Every other organization space is a flat list. Cheer is a hierarchy (a GYM has
 * ~10 TEAMS, each of which can win MANY championships) AND a partnership funnel
 * (target → signed contract → exclusive branded jewelry program) at the same
 * time. The model lives in `cheer.js`; this kind is the switch that turns it on.
 */
export { CHEER_PARTNERSHIP } from './cheer.js'
import { CHEER_PARTNERSHIP } from './cheer.js'

/**
 * EVERY KIND THAT IS AN ORGANIZATION RATHER THAN AN EVENT.
 *
 * A police department, a fire department, a Greek chapter and a corporation all
 * share one property that a championship does not have: nobody wins anything.
 * There is no sport, no event date, no countdown, no champion and no incumbent
 * RING VENDOR. Every shared view keys off this set to drop that framing, so a
 * space added with any of these kinds is correctly non-competitive on day one
 * instead of rendering an organization through a championship's columns.
 */
export const ORG_KINDS = [EMPLOYEE_RECOGNITION, COMMUNITY_HERO, GREEK_PURSUIT, CHEER_PARTNERSHIP]
const ORG_KIND_SET = new Set(ORG_KINDS)

/** Is this KIND (from a category or a record) an organization rather than an event? */
export function isOrgKind(kind) {
  return ORG_KIND_SET.has(kind)
}

/**
 * THE SAFE GENERIC NOUN.
 *
 * ONLY the Championships space may say "championship". A police department, a
 * fire department, a corporation, an agency, a military unit, a Greek chapter,
 * an RFP and a letterwinner opportunity are NOT championships, and calling one
 * a championship on a shared surface (the map's markers, tooltips, legend and
 * counts) is a bug, not a cosmetic slip. So the fallback for a space that
 * declares no noun of its own is "opportunity / opportunities" — never the
 * championship wording. A future space (halls of fame, bands, conference, …)
 * therefore reads correctly the moment it is added, even before anyone gets
 * round to giving it its own noun.
 *
 * The Letterwinner space declares this word EXPLICITLY rather than leaning on
 * the fallback: it is a MAJOR OPPORTUNITY — an alumni letterwinner program, not
 * an event with a winner — and "opportunity / opportunities" is the true noun
 * for it, not a default nobody got round to replacing.
 */
export const GENERIC_NOUN = 'opportunity'
export const GENERIC_NOUN_PLURAL = 'opportunities'

export const CATEGORIES = [
  {
    key: 'championships',
    label: 'Championships',
    noun: 'Championship',
    nounPlural: 'championships',
    // The ONLY space allowed to say "championship" on the map.
    mapNoun: 'championship',
    mapNounPlural: 'championships',
    source: 'championships',
    views: CHAMPIONSHIP_VIEWS,
    writes: true,
    accent: '#a5762a',
    blurb:
      'The full championship universe — action-first pursuit views over live HubSpot data. Click any championship for its full record and the action console.',
    // Verbatim pre-rail copy. Do not "improve" these — the Championships space
    // is required to read exactly as it did before the rail was added.
    lead:
      'The full championship universe — action-first pursuit views over live HubSpot data. Click any championship for its full record and the action console. Read-only on the record.',
    exportTitle: 'championship opportunities',
    recordsLabel: 'championship records',
  },
  {
    key: 'rfp',
    label: 'RFPs',
    noun: 'RFP',
    nounPlural: 'RFPs',
    // A solicitation, not a championship — the map counts bids.
    mapNoun: 'RFP',
    mapNounPlural: 'RFPs',
    source: 'pursuits',
    line: 'rfp',
    // Deadline-driven: RFP deals carry a real close date (HubSpot `closedate`
    // on pipeline 914387521), so the date-spine views are honest here.
    views: DEADLINE_VIEWS,
    // What that date IS. The backend declares the same thing on `config`
    // (`dateProp: 'closeDate'`); this is the fallback so a stale config can
    // never make a deadline read as an event date.
    dateKind: 'close',
    dateLabel: 'Close date',
    accent: '#3f6f9a',
    blurb: 'Surfaced bids and solicitations from the RFP engine, counted down to their close date. Read-only in the Command Center.',
    // An RFP with no path to submit is useless (Brian, 2026-08-04): every RFP
    // row surfaces its official solicitation / submission URL as a real link.
    // See `pursuitUrl()` in pursuitAdapter.js for how the field is resolved.
    submitLabel: 'View / Submit RFP',
  },
  {
    // GREEK IS PURSUIT TRACKING (Brian, 2026-08-05). A fraternity or sorority
    // never wins anything either — what a rep works here is a list of PRODUCTS
    // being pursued at each organization, each with an owner, a date the pursuit
    // opened and, when it lands, a date it was WON. Those four columns are the
    // space, and every one of them is sourced from a real associated pursuit
    // DEAL (see `recognition.js`) — never derived, never estimated.
    //
    // NATIONALS ABOVE CHAPTERS. A national organization and one of its chapters
    // are completely different-sized opportunities, so `sr_org_type` groups the
    // nationals at the top of every board rather than scattering them among
    // hundreds of chapters.
    key: 'greek',
    label: 'Greek',
    sublabel: 'Pursuit Tracking',
    title: 'Greek — Pursuit Tracking',
    kind: GREEK_PURSUIT,
    noun: 'Chapter',
    nounPlural: 'chapters',
    mapNoun: 'chapter',
    mapNounPlural: 'chapters',
    source: 'pursuits',
    line: 'greek',
    views: UNDATED_VIEWS,
    accent: '#8a5db8',
    blurb:
      'Fraternity and sorority organizations — badges, chapter rings and recognition jewelry. Each row carries the product being pursued, who owns it, when the pursuit opened and when it was won. Nationals are grouped above their chapters.',
    // A Greek organization's official channel is its own site, the same as a
    // corporate account's — not a solicitation posting.
    submitLabel: 'Open organization website',
    linkLabel: 'Website',
  },
  {
    // CORPORATE IS EMPLOYEE RECOGNITION — NOT A CHAMPIONSHIP (Brian,
    // 2026-08-05). A corporation never wins anything: it runs an ongoing
    // service-award / milestone / years-of-service program. So this space is
    // sized by HEADCOUNT, qualified by INDUSTRY and located by HQ, and every
    // championship affordance — sport, event date, countdown, crown,
    // Submit-for-Win, incumbent ring vendor — is absent rather than blank.
    // `kind` is what the views key off; the backend declares the same word on
    // `config.kind`, so the two can't drift.
    key: 'corporate',
    label: 'Corporate',
    sublabel: 'Employee Recognition',
    title: 'Corporate — Employee Recognition',
    kind: EMPLOYEE_RECOGNITION,
    noun: 'Company',
    nounPlural: 'companies',
    // A corporation runs a recognition PROGRAM — it never wins anything. The
    // map counts accounts in a state, never "championships".
    mapNoun: 'account',
    mapNounPlural: 'accounts',
    source: 'pursuits',
    line: 'corporate',
    // No dated view: a corporate account has no event to count down to, so the
    // 7/30/60/90 lens, the worklist buckets, the month grid and Just Won are
    // all absent rather than rendered as one enormous "NO DATE" pile.
    views: UNDATED_VIEWS,
    accent: '#4a8f6e',
    blurb:
      'Employee recognition accounts — corporations sized by headcount, qualified by industry and located by HQ. Service awards, milestones and years-of-service programs, not championships. Read-only in the Command Center.',
    // The corporate "official channel" is the company's own site — the way a
    // rep actually researches and reaches the account.
    submitLabel: 'Open company website',
    linkLabel: 'Website',
  },
  {
    // POLICE IS A COMMUNITY HERO RECOGNITION PROGRAM — NOT A CHAMPIONSHIP
    // (Brian, 2026-08-05). A precinct never competes and never wins: what it
    // has is officers with decades of service, and SR's Community Hero program
    // recognises ONE of them per department with a ring or pendant at NO COST.
    // So the space is framed around service and recognition, every championship
    // affordance is absent rather than blank, and the headline column is
    // whether that department has a hero yet.
    key: 'police',
    label: 'Police',
    sublabel: 'Community Hero',
    title: 'Police — Community Hero Recognition',
    kind: COMMUNITY_HERO,
    noun: 'Department',
    nounPlural: 'departments',
    mapNoun: 'department',
    mapNounPlural: 'departments',
    source: 'pursuits',
    line: 'police',
    views: UNDATED_VIEWS,
    accent: '#2f6e8f',
    blurb:
      'Law-enforcement agencies and the SR Community Hero program — one nominated hero per precinct, recognised for years of service with a ring or pendant provided at no cost. Service awards and retirement recognition, not a championship.',
    submitLabel: 'Open department website',
    linkLabel: 'Website',
    heroLabel: 'Community Hero',
    honoreeNoun: 'officer',
  },
  {
    key: 'fire',
    label: 'Fire',
    sublabel: 'Community Hero',
    title: 'Fire — Community Hero Recognition',
    kind: COMMUNITY_HERO,
    noun: 'Department',
    nounPlural: 'departments',
    mapNoun: 'department',
    mapNounPlural: 'departments',
    source: 'pursuits',
    line: 'fire',
    views: UNDATED_VIEWS,
    accent: '#b5502e',
    blurb:
      'Fire and rescue departments and the SR Community Hero program — one nominated hero per station or department, recognised for years of service with a ring or pendant provided at no cost. Service awards and retirement recognition, not a championship.',
    submitLabel: 'Open department website',
    linkLabel: 'Website',
    heroLabel: 'Community Hero',
    honoreeNoun: 'firefighter',
  },
  {
    key: 'government',
    label: 'Government',
    noun: 'Agency',
    nounPlural: 'agencies',
    mapNoun: 'agency',
    mapNounPlural: 'agencies',
    source: 'pursuits',
    line: 'government',
    views: UNDATED_VIEWS,
    accent: '#6e5a3a',
    blurb: 'Public-sector agencies from the government engine. Read-only in the Command Center.',
  },
  {
    key: 'military',
    label: 'Military',
    noun: 'Unit',
    nounPlural: 'units',
    mapNoun: 'unit',
    mapNounPlural: 'units',
    source: 'pursuits',
    line: 'military',
    views: UNDATED_VIEWS,
    /* ⚠️ OFF `pending-data` — MEASURED 2026-09-14, THE LINE SERVES 60 RECORDS.
     *
     * `pending-data` means "not sourced yet: the tab is real, it says the data
     * is being fed in, and it never fetches a line that isn't there". That was
     * true once and stopped being true without anybody noticing, so the space
     * was telling its own users their records were still coming while the
     * backend had been serving them. Exactly the state police / fire / greek
     * were left in before v1.29, and the reason the flag is dropped "the day
     * the line returns records" rather than on a later tidy-up.
     *
     * ⚠️ AND THE COUNT WAS NOT WHAT THE DOCS SAID. PURSUIT-WRITE-PLAN §3 records
     * 52; paging the cursor to completion returns **60**. Government and Greek
     * are stale in the same direction (277 → 291, 165 → 206), so the universes
     * are growing and no figure in that table should be quoted without a
     * re-measure. Flipping on the doc's number would have been flipping on a
     * number nobody had checked — which is the thing this flag is being
     * corrected for in the first place.
     *
     * A live line that answers ZERO gets the honest empty card (`SpaceEmpty`),
     * which is a different statement from "being fed in" and needs no flag. */
    accent: '#556070',
    blurb: 'Military units and installations — challenge coins, unit awards and commemoratives.',
  },
  {
    // LETTERWINNER IS A MAJOR OPPORTUNITY — NOT A CHAMPIONSHIP. An alumni
    // letterwinner program is an ongoing institutional partnership: every
    // former letter-earner is a ring buyer, year after year. There is no event,
    // no date and nobody wins anything, so the space carries the GENERIC noun
    // ("opportunity / opportunities") explicitly and every championship
    // affordance — sport, event date, countdown, champion, crown — is absent
    // rather than blank.
    //
    // The data line ALREADY EXISTS: `letterwinner` is the pursuit tracker's
    // original company-sourced engine (see `Pursuits.jsx`, `PURSUIT_LINES`).
    // This space reuses that feed through the same adapter every other pursuit
    // space uses — no new scan, no new endpoint, no new record shape.
    key: 'letterwinner',
    label: 'Letterwinner',
    sublabel: 'Major Opportunity',
    title: 'Letterwinner — Major Opportunity',
    noun: 'Opportunity',
    nounPlural: 'opportunities',
    // Stated outright, not inherited: an alumni letterwinner program is an
    // opportunity in every count, marker and tooltip the shared map draws.
    mapNoun: 'opportunity',
    mapNounPlural: 'opportunities',
    source: 'pursuits',
    line: 'letterwinner',
    // No dated view: a letterwinner program has no event to count down to, so
    // the 7/30/60/90 lens, the worklist buckets, the month grid and Just Won
    // are all absent rather than rendered as one enormous "NO DATE" pile.
    views: UNDATED_VIEWS,
    accent: '#a33f4e',
    blurb:
      'Alumni letterwinner programs — institutions sized by their living letterwinner base, ranked by tier and value band. A standing recognition opportunity, not a championship. Read-only in the Command Center.',
  },
  {
    // CHEER IS ITS OWN ANIMAL — A GYM-PARTNERSHIP MACHINE (Brian, 2026-08-06).
    //
    // Not a championship line, and not another flat organization list. The
    // record is a GYM; a gym runs ~10 TEAMS; a team can win MANY championships.
    // The CONTRACT and the EXCLUSIVE, FULLY-BRANDED JEWELRY PROGRAM live at the
    // gym; the championship pursuits — the actual engine — roll UP from its
    // teams' titles.
    //
    // The job is the whole TAM: every cheer gym is either a PARTNER or a
    // TARGET, and the space exists to move targets along the pipeline. That is
    // why it declares its own view (`partnership`) FIRST — the space opens on
    // the funnel and the next step per gym, never on a table of rows.
    //
    // Appended last, so the nine spaces before it keep their exact rail
    // positions. See `cheer.js` for the model and `verify-spaces.mjs` §17.
    key: 'cheer',
    label: 'Cheer',
    sublabel: 'Gym Partnerships',
    title: 'Cheer — Gym Partnerships',
    kind: CHEER_PARTNERSHIP,
    noun: 'Gym',
    nounPlural: 'gyms',
    // A gym is not a championship. Its TEAMS win championships; the gym itself
    // is a partnership, so the map counts gyms.
    mapNoun: 'gym',
    mapNounPlural: 'gyms',
    source: 'pursuits',
    line: 'cheer',
    // The gym-partnership pipeline first, then the shared undated views. A gym
    // has no event date of its own — the titles do, and those live on the
    // championships plane — so the 7/30/60/90 lens stays out.
    views: GYM_VIEWS,
    accent: '#c2557f',
    blurb:
      'Every cheer gym in the TAM, as a partnership pipeline: target → signed contract → exclusive fully-branded jewelry program. Each gym carries its teams and the championships they have won, so the strongest championship footprints are the ones to go after first.',
    submitLabel: 'Open gym website',
    linkLabel: 'Website',
  },
  /**
   * ⚠️ APPENDED AS THE ELEVENTH, NOT INSERTED. Letterwinner went in ninth and
   * Cheer tenth under the same rule — a space that already has a rail position
   * does not move, because people navigate by muscle memory and `verify-spaces`
   * pins the order for exactly that reason. My first pass put this second and
   * the suite failed on four assertions, which is the check working.
   *
   * BOWL SEASON — Brian: "Bowl season can be its own area."
   *
   * He is right that it does not belong inside Championships. A bowl is a
   * different animal from a title: nobody QUALIFIES for it by winning, teams are
   * SELECTED and invited, both sides get rings, and the whole thing happens in a
   * four-week window in December and January. Buried among 14,000 year-round
   * championships it is invisible for eleven months and then urgent.
   *
   * ⚠️ THE FILTER IS STRUCTURAL, AND THAT MATTERS MORE THAN IT LOOKS.
   * `sr_segment === 'Bowl'` gives exactly the 42 college football bowls. Filtering
   * on the NAME containing "Bowl" would have given 92 — sweeping in 27 high-school
   * ACADEMIC quiz-bowl championships (Scholastic Bowl, Scholars Bowl, Scholar
   * Bowl, Academic Bowl), the NFL Super Bowl, and the Pop Warner Super Bowl. A
   * rep opening Bowl Season to find Kansas Scholars Bowl 4A would rightly stop
   * trusting the space.
   *
   * The CFP National Championship is included by name and it is the ONE exception
   * to the structural rule, deliberately: it is not itself a bowl — the
   * semifinals rotate through the New Year's Six — but it is the largest ring
   * order of the season, and a bowl-season view that omitted it would be wrong
   * for the person using it.
   */
  {
    key: 'bowls',
    label: 'Bowl Season',
    sublabel: 'College Football',
    noun: 'Bowl game',
    nounPlural: 'bowl games',
    mapNoun: 'bowl game',
    mapNounPlural: 'bowl games',
    source: 'championships',
    /* Bowls carry real, published dates, so the date spine is the whole point —
       the countdown IS the product here.

       ⚠️ FULL FUNCTIONALITY, and my first pass had this WRONG. I shipped it
       read-only reasoning that "two spaces writing the same record is how a
       crown gets overwritten". That is not a real hazard: it is the SAME HubSpot
       record either way, written by the same human click, and `allowsWrites`
       already refuses any space that is not championship-sourced. Making a rep
       switch tabs to act on a record they are looking at is friction with no
       safety behind it. Brian: "needs full functionality and integration between
       every single tab is Paramount." */
    views: CHAMPIONSHIP_VIEWS,
    writes: true,
    accent: '#8B5E2A',
    recordFilter: (r) =>
      String(r?.raw?.sr_segment ?? r?.segment ?? '') === 'Bowl'
      || /College Football Playoff National Championship|CFP National Championship/i.test(
        String(r?.name ?? r?.raw?.championship_name ?? ''),
      ),
    blurb:
      'Every college football bowl game and the CFP National Championship, on their own countdown. Both teams in a bowl get rings — this is the only space where the loser is still a customer.',
    lead:
      'Every college football bowl game and the CFP National Championship, on their own countdown. Teams are SELECTED, not qualified, and BOTH sides get rings — so a bowl is two opportunities, not one. Read-only on the record: these are the same HubSpot records the Championships space owns.',
    exportTitle: 'bowl games',
    recordsLabel: 'bowl games',
  },


  /**
   * POSTSEASON RUNS — the opportunities that are NOT a title.
   *
   * Brian: "final four sweet 16 team still get rings in other sports if they
   * make big tournaments as it's a major accomplishment. I want you to solidify
   * all of the opportunities and make sure that every single one of them is
   * logged."
   *
   * ⚠️ THIS SPACE EXISTS BECAUSE THE OLD MODEL HAD AN ASSUMPTION BURIED IN IT:
   * that only WINNING is commemorated. A championship record is one title and
   * one winner, so a Final Four team, a College World Series team and a Sweet 16
   * team were all invisible — not under-served, absent.
   *
   * The arithmetic is why it earns a tab. A championship is ONE customer. These
   * rounds reach 64, 32, 16, 8, 4, so a single tournament is dozens of orders,
   * and the team count rides in the division label because it is the number that
   * decides whether a round is worth working.
   *
   * Fully functional and championship-sourced, same as Bowl Season: these are
   * records on 2-64425303, so the crown console, the drawer and the assignment
   * engine all work on them without a second implementation.
   */
  {
    key: 'postseason',
    label: 'Postseason Runs',
    sublabel: 'Rounds & Berths',
    noun: 'Postseason run',
    nounPlural: 'postseason runs',
    mapNoun: 'postseason run',
    mapNounPlural: 'postseason runs',
    source: 'championships',
    views: CHAMPIONSHIP_VIEWS,
    writes: true,
    accent: '#3E6B8B',
    recordFilter: (r) =>
      String(r?.raw?.sr_level ?? r?.level ?? '') === 'Tournament Round',
    blurb:
      'Every named postseason round — Final Four, NCAA Sweet 16, the College World Series, the Frozen Four. Reaching one is the accomplishment; the winner is a different record.',
    lead:
      'Every named postseason round a school commemorates — Final Four, NCAA Sweet 16, the College World Series, the Frozen Four, the College Cup. A championship is one customer; a Sweet 16 is sixteen. The team count is on every row because it is what decides whether a round is worth working.',
    exportTitle: 'postseason runs',
    recordsLabel: 'postseason runs',
  },

]

export const DEFAULT_CATEGORY_KEY = 'championships'

export const CATEGORY_BY_KEY = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]))

/** The category for a key, falling back to the default rather than undefined. */
export function categoryFor(key) {
  return CATEGORY_BY_KEY[key] || CATEGORY_BY_KEY[DEFAULT_CATEGORY_KEY]
}

/** Is this space still waiting on its data line? */
export function isPending(category) {
  return category?.status === PENDING
}

/** Does this space allow the championship write actions (crown / dossier)? */
export function allowsWrites(category) {
  return category?.source === 'championships' && category?.writes === true
}

/* ══ THE PURSUIT FIELD EDITOR — A SEPARATE GATE, DELIBERATELY ══════════════
 *
 * Brian: "these all must have read, and write functionality." And: "widen it
 * deliberately and per-space, not globally."
 *
 * ⚠️ THIS IS NOT `allowsWrites` AND MUST NEVER BE FOLDED INTO IT. That function
 * governs the CHAMPIONSHIP writes — crown, Submit-for-Win, the Champion-to-Close
 * dossier — which target HubSpot 2-64425303, a record no pursuit row has. It
 * returns true for exactly one space and `verify-spaces.mjs` §7 pins that,
 * including the exact lead string. Widening it to let a corporate account be
 * field-corrected would hand every pursuit space a crown button pointed at an
 * object it has no record in. Two different write systems, two different gates.
 *
 * ⚠️ AND IT IS AN OPT-IN PER SPACE, NOT A PROPERTY OF "BEING A PURSUIT SPACE".
 * A space is listed here only once `GET /api/pursuit-fields?line=<line>` has
 * been driven live for it and its policy read. Four are, measured 2026-09-14:
 *
 *   corporate    1,783 accounts · companies · 8 operational + 5 privileged
 *   government     277 agencies · same shape, same `segmentRow` server-side
 *   greek          165 chapters · same
 *   military        52 units    · same
 *
 * Those four share one row mapper backend-side, so #2 genuinely came free with
 * #1 — which is why they ship together and nothing else does yet.
 *
 * Deliberately NOT listed, each for its own reason rather than by omission:
 *
 *   police · fire   The biggest universe (the repo cites ~26k fire departments)
 *                   and the WORST fit for a per-record editor — nobody
 *                   hand-assigns 26,000 departments. That is a bulk rule job and
 *                   the pattern already exists (`POST /api/cheer/assign` moved
 *                   519 records on a rule at SYSTEM_RUN). The editor is for the
 *                   exceptions afterwards. Ordering by record count would put
 *                   the editor exactly where it cannot help.
 *   cheer           Its privileged tier carries an extra field (`sr_org_id`) and
 *                   the jewelry properties it most needs corrected are recorded
 *                   as not existing on the portal at all. It wants its own pass.
 *   rfp             The first `deals` line — a different objectType, a different
 *                   field set, and a `never` list containing `dealstage` and
 *                   `pipeline`. The consequence is also the highest (a wrong
 *                   close date or a dead submission URL loses a bid). It proves
 *                   the objectType axis and deserves its own measurement, not a
 *                   flag flipped on the way past.
 *   letterwinner    NOT a HubSpot line at all. `GET /api/pursuit-fields` answers
 *                   400 `unknown_line` for it BY NAME — it is written through
 *                   the `/api/pursuits-update` proxy against sr-pursuit-tracker,
 *                   a different system holding a different universe. A flag here
 *                   would draw an editor that posts into the wrong portal.
 *   championships   Has its own editor (`FieldCorrect.jsx`) against its own
 *                   route. `canEditPursuitFields` requires `record.sourceType`,
 *                   which a championship never carries, so the two can never
 *                   both draw on one record.
 */
export const FIELD_EDIT_LINES = ['corporate', 'government', 'greek', 'military']

export function allowsFieldEdits(category) {
  return category?.source === 'pursuits' && FIELD_EDIT_LINES.includes(category?.line)
}

/** The view definitions this space renders, in canonical order. */
export function viewsFor(category) {
  const allowed = new Set(category?.views || ALL_VIEWS)
  return VIEW_DEFS.filter((v) => allowed.has(v.key))
}

/** First view of a space — what a category switch lands on when the current view is gone. */
export function defaultViewFor(category) {
  return viewsFor(category)[0]?.key || 'board'
}

/**
 * The page lead for a space. Championships carries its verbatim pre-rail lead;
 * every other space describes itself and says the surface is scoped to it.
 */
export function leadFor(category) {
  if (category?.lead) return category.lead
  // The space's FULL title, so "Corporate" reads as the employee-recognition
  // surface it is rather than as another championship board.
  return `${category?.blurb || ''} Every view, filter and export below is scoped to the ${titleFor(category)} space.`
}

/** What the load summary calls a record in this space. */
export function recordsLabelFor(category) {
  return category?.recordsLabel || category?.nounPlural || 'records'
}

/** The CSV panel's title noun for this space. */
export function exportTitleFor(category) {
  return category?.exportTitle || category?.nounPlural || 'records'
}

/** The label on a record's official-source link in this space. */
export function submitLabelFor(category) {
  return category?.submitLabel || 'Open official listing'
}

/**
 * What this space's effective date is CALLED. Championships count down to an
 * event; RFPs count down to a close date. Column headers and the export read
 * this, so no view hardcodes "Event date".
 */
export function dateLabelFor(category) {
  return category?.dateLabel || 'Event date'
}

/**
 * Does this space have a DATE SPINE at all?
 *
 * Derived from the views it declares rather than stated twice: a space that
 * renders no dated view has no date to render, so every date affordance the
 * shared views carry — the event-date column, the countdown, the "NO DATE" gap
 * bucket — is switched off instead of showing a blank for every record.
 * Championships (event date) and RFPs (close date) are dated; Corporate and the
 * other organization spaces are not.
 */
export function isDatedSpace(category) {
  const allowed = new Set(category?.views || ALL_VIEWS)
  return VIEW_DEFS.some((v) => v.dated && allowed.has(v.key))
}

/** Is this an employee-recognition (organization) space rather than an event one? */
export function isEmployeeRecognition(category) {
  return category?.kind === EMPLOYEE_RECOGNITION
}

/** Is this a Community Hero space (police / fire)? */
export function isCommunityHero(category) {
  return category?.kind === COMMUNITY_HERO
}

/** Is this the Greek pursuit-tracking space? */
export function isGreekPursuit(category) {
  return category?.kind === GREEK_PURSUIT
}

/** Is this the Cheer gym-partnership space? */
export function isCheerPartnership(category) {
  return category?.kind === CHEER_PARTNERSHIP
}

/**
 * Is this space an ORGANIZATION space rather than an event one? True for
 * Corporate, Police, Fire and Greek — every one of which has no sport, no event
 * date, no countdown, no champion and no incumbent ring vendor.
 */
export function isOrgSpace(category) {
  return isOrgKind(category?.kind)
}

/** What this space calls its recognised honoree program. */
export function heroLabelFor(category) {
  return category?.heroLabel || 'Community Hero'
}

/** What this space calls the person being honoured ("officer" / "firefighter"). */
export function honoreeNounFor(category) {
  return category?.honoreeNoun || 'honoree'
}

/**
 * The space's full title — "Corporate — Employee Recognition". The rail keeps
 * the short `label`; the page header and the empty card say what the space
 * actually IS, so nobody reads Corporate as another championship board.
 */
export function titleFor(category) {
  return category?.title || category?.label || ''
}

/** The column/CTA heading for a record's official link in this space. */
export function linkLabelFor(category) {
  return category?.linkLabel || 'Official listing'
}

/**
 * WHAT A RECORD IS CALLED on a shared surface — the map's title, hint, markers,
 * tooltips and counts, and the manager rollup's totals.
 *
 * Resolution order, and the whole point of it: the space's own `mapNoun`, then
 * its `noun`, then the GENERIC noun — "opportunity / opportunities". The
 * championship wording is NEVER a fallback. Before this existed the map printed
 * "championships" for every space, so a police department, a corporate account
 * and an RFP all read as championships they are not.
 *
 * The Letterwinner space is the case the fallback was written for: it is a MAJOR
 * opportunity, not a championship. It now declares `opportunity /
 * opportunities` outright — the same word the fallback would have produced —
 * so the map, the manager rollup and the CSV name column read "opportunities",
 * which is true, rather than "championships", which is false. The fallback
 * stays for the space after it.
 */
export function mapNounFor(category) {
  return {
    singular: category?.mapNoun || category?.noun || GENERIC_NOUN,
    plural: category?.mapNounPlural || category?.nounPlural || GENERIC_NOUN_PLURAL,
  }
}

/** The singular noun for a record in this space, on a shared surface. */
export function nounFor(category) {
  return mapNounFor(category).singular
}

/** The plural noun for records in this space, on a shared surface. */
export function nounPluralFor(category) {
  return mapNounFor(category).plural
}
